import { Heading } from '@dittolive/anvil'
import { graphql } from 'babel-plugin-relay/macro'
import { TenantContext } from 'components/tenant'
import { useContext, useEffect } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { useAppManagementContext } from 'scenes/appManagement/context'

import { AppDangerSectionQuery } from './__generated__/AppDangerSectionQuery.graphql'
import AppVisibilitySection from './AppVisibilitySection'
import DeleteAppSection from './DeleteAppSection'
import TransferAppSection from './TransferAppSection'

export const query = graphql`
  query AppDangerSectionQuery($appId: ID!) {
    viewer {
      me {
        isSuperAdmin
      }
      appById(id: $appId) {
        viewerOwnsApp
        viewerCanDelete
        viewerCanTransfer
        transferRequestDestination {
          __typename
        }
      }
    }
  }
`

/** Section with the controls required for deleting an app */
export default function AppDangerSectionContainer() {
  const { appId } = useAppManagementContext()
  const [queryRef, loadQuery] = useQueryLoader<AppDangerSectionQuery>(query)

  useEffect(() => {
    loadQuery({ appId }, { fetchPolicy: 'store-and-network' })
  }, [appId, loadQuery])

  if (!queryRef) {
    return null
  }

  return <AppDangerSection queryRef={queryRef} />
}

type Props = {
  queryRef: PreloadedQuery<AppDangerSectionQuery>
}

function AppDangerSection({ queryRef }: Props) {
  const {
    viewer: { appById: app, me },
  } = usePreloadedQuery<AppDangerSectionQuery>(query, queryRef)
  const { tenant } = useContext(TenantContext)

  const appIsInOrganization = !!tenant
  const viewerHasOwnerPrivileges = !!me?.isSuperAdmin || !!app?.viewerOwnsApp

  const isActionPossible =
    viewerHasOwnerPrivileges ||
    !!app?.viewerCanTransfer ||
    !!app?.transferRequestDestination ||
    !!app?.viewerCanDelete

  if (!app || !isActionPossible) {
    return null
  }

  return (
    <div className="mt-20 flex flex-col gap-6">
      <div>
        <Heading level={2}>Danger zone</Heading>
        <p className="text-foreground-subtle font-sans">
          Delete your database entirely or transfer it to another person or
          organization. Be cautious when taking action here.
        </p>
      </div>

      {viewerHasOwnerPrivileges && appIsInOrganization && (
        <AppVisibilitySection />
      )}
      {(app.viewerCanTransfer || !!app.transferRequestDestination) && (
        <TransferAppSection />
      )}
      {app.viewerCanDelete && <DeleteAppSection />}
    </div>
  )
}
